"use client";

import { useState } from "react";

import { getPalette } from "@/lib/palette";
import { usePrefersDark } from "@/lib/usePrefersDark";

export type ObservedSmokeRow = {
  kind: "observed_smoke";
  year: number;
  hours: number;
};

export type ConvectiveCountRow = {
  kind: "convective_count";
  indicator: "events" | "conditions";
  label: string;
  unit: string;
  historical_adjusted: number;
  value_2050s_p10: number;
  value_2050s_median: number;
  value_2050s_p90: number;
  value_2080s_p10: number;
  value_2080s_median: number;
  value_2080s_p90: number;
};

export type MedianIncreaseRow = {
  kind: "median_increase";
  label: string;
  increase_2050s_pct: number;
  increase_2080s_pct: number;
  ensemble: string;
};

export type SeasonWindowRow = {
  kind: "season_window";
  period: "historical_adjusted" | "2050s" | "2080s";
  label: string;
  start_month: number;
  end_month: number;
};

export type SectionQuoteRow = {
  kind: "section_quote";
  section: string;
  page: number;
  verbatim: string;
};

export type SmokeLongRunAverageRow = {
  kind: "smoke_long_run_average";
  value_hours: number;
  source: string;
};

type Props = {
  observed: ObservedSmokeRow[];
  longRunAverage: number;
  height?: number;
};

const VIEW_W = 1000;
const PAD_TOP = 28;
const PAD_BOTTOM = 56;
const PAD_LEFT = 60;
const PAD_RIGHT = 16;

/**
 * Annual smoke hours at YYC, 1990-present (City Layer 12). Bars show
 * each year's count of hours that smoke (from any wildfire in the long-
 * range transport corridor) was logged as a weather phenomenon at the
 * airport. A horizontal reference line at the published long-run mean
 * anchors the post-2015 escalation visually.
 */
export function ClimateStormsChart({
  observed,
  longRunAverage,
  height = 360,
}: Props) {
  const p = getPalette(usePrefersDark());
  const [hover, setHover] = useState<number | null>(null);

  const obs = [...observed].sort((a, b) => a.year - b.year);
  const allValues = [...obs.map((r) => r.hours), longRunAverage, 0];
  const yMin = 0;
  const yMax = Math.ceil(Math.max(...allValues) / 100) * 100 + 50;

  const VIEW_H = height;
  const plotTop = PAD_TOP;
  const plotBottom = VIEW_H - PAD_BOTTOM;
  const plotLeft = PAD_LEFT;
  const plotRight = VIEW_W - PAD_RIGHT;
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;

  const yToPx = (v: number) =>
    plotBottom - ((v - yMin) / (yMax - yMin)) * plotH;

  const yearSpan = obs.length > 0 ? obs[obs.length - 1].year - obs[0].year : 1;
  const barSlotW = plotW / Math.max(yearSpan + 1, 1);
  const barW = Math.max(barSlotW * 0.7, 2);
  const xForYear = (y: number) =>
    obs.length === 0
      ? plotLeft
      : plotLeft +
        ((y - obs[0].year) / Math.max(yearSpan, 1)) * (plotW - barSlotW) +
        barSlotW / 2;

  const gridLines: number[] = [];
  for (let v = yMin; v <= yMax; v += 100) gridLines.push(v);

  const baselineY = yToPx(longRunAverage);

  return (
    <div className="cs-chart">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="w-full h-auto"
        role="img"
        aria-label={`Calgary annual smoke hours at YYC, ${obs[0]?.year ?? ""}-${obs.at(-1)?.year ?? ""}, with the long-run mean as a baseline`}
      >
        {gridLines.map((g) => {
          const y = yToPx(g);
          return (
            <g key={g}>
              <line
                x1={plotLeft}
                x2={plotRight}
                y1={y}
                y2={y}
                stroke="var(--rule)"
                strokeDasharray="2 4"
                strokeWidth={0.6}
              />
              <text
                x={plotLeft - 10}
                y={y}
                fontSize={11}
                fontFamily="var(--font-recursive)"
                fill="var(--ink-3)"
                textAnchor="end"
                dominantBaseline="central"
              >
                {g}
              </text>
            </g>
          );
        })}
        <text
          x={plotLeft - 10}
          y={plotTop - 12}
          fontSize={10}
          fontFamily="var(--font-recursive)"
          fill="var(--ink-3)"
          textAnchor="end"
        >
          hours/year
        </text>

        {/* Long-run mean baseline. */}
        <line
          x1={plotLeft}
          x2={plotRight}
          y1={baselineY}
          y2={baselineY}
          stroke={p.ink}
          strokeDasharray="6 4"
          strokeWidth={1}
          opacity={0.65}
        />
        <text
          x={plotRight - 8}
          y={baselineY - 6}
          fontSize={10.5}
          fontFamily="var(--font-recursive)"
          fill="var(--ink-2)"
          textAnchor="end"
        >
          1990-present mean · {longRunAverage} hr/yr
        </text>

        {/* Bars. Each year is a focus target with a focus ring. */}
        {obs.map((r) => {
          const x = xForYear(r.year);
          const y = yToPx(r.hours);
          const h = plotBottom - y;
          const isHover = hover === r.year;
          return (
            <g
              key={r.year}
              tabIndex={0}
              role="img"
              aria-label={`${r.year}: ${r.hours} smoke hours at YYC`}
              onMouseEnter={() => setHover(r.year)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(r.year)}
              onBlur={() => setHover(null)}
              style={{ outline: "none" }}
            >
              <rect
                x={x - barW / 2}
                y={y}
                width={barW}
                height={Math.max(h, 0.5)}
                fill={p.signal}
                opacity={isHover ? 0.95 : 0.78}
                rx={1}
              />
              {isHover ? (
                <>
                  <rect
                    x={x - barW / 2 - 2}
                    y={y - 2}
                    width={barW + 4}
                    height={Math.max(h, 0.5) + 4}
                    fill="none"
                    stroke="var(--ink)"
                    strokeWidth={1.5}
                    rx={2}
                    pointerEvents="none"
                  />
                  <rect
                    x={x - 50}
                    y={y - 30}
                    width={100}
                    height={22}
                    rx={3}
                    fill="var(--bg)"
                    stroke="var(--rule)"
                    strokeWidth={0.6}
                    opacity={0.96}
                  />
                  <text
                    x={x}
                    y={y - 16}
                    fontSize={11}
                    fontFamily="var(--font-feature)"
                    fontWeight={700}
                    fill="var(--ink)"
                    textAnchor="middle"
                  >
                    {r.year}: {r.hours} hr
                  </text>
                </>
              ) : null}
            </g>
          );
        })}

        {obs.length > 0 ? (
          <>
            <text
              x={xForYear(obs[0].year)}
              y={plotBottom + 16}
              fontSize={11}
              fontFamily="var(--font-recursive)"
              fill="var(--ink-3)"
              textAnchor="start"
            >
              {obs[0].year}
            </text>
            <text
              x={xForYear(obs[obs.length - 1].year)}
              y={plotBottom + 16}
              fontSize={11}
              fontFamily="var(--font-recursive)"
              fill="var(--ink-3)"
              textAnchor="end"
            >
              {obs[obs.length - 1].year}
            </text>
            <text
              x={(xForYear(obs[0].year) + xForYear(obs[obs.length - 1].year)) / 2}
              y={plotBottom + 32}
              fontSize={10}
              fontFamily="var(--font-recursive)"
              fill="var(--ink-3)"
              textAnchor="middle"
            >
              wildfire-smoke hours observed at YYC · ArcGIS Layer 12
            </text>
          </>
        ) : null}
      </svg>
    </div>
  );
}
